import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Slider, Switch, Button, Tabs, Tooltip, Space, Alert } from 'antd';
import Icon from '@components/Icon';
import stores from '@stores';

// 滤镜预设方案
const filterPresets = [
    { 
        name: '原图', 
        icon: <Icon.ImagePlay size={18} />,
        filter: {
            blur: 0,
            brightness: 100,
            contrast: 100,
            grayscale: 0,
            hueRotate: 0,
            saturate: 100,
            sepia: 0,
            invert: 0,
        }
    },
    { 
        name: '黑白', 
        icon: <Icon.FileText size={18} />,
        filter: {
            grayscale: 100,
            contrast: 120,
            brightness: 110,
        }
    },
    { 
        name: '复古', 
        icon: <Icon.RotateCcw size={18} />,
        filter: {
            sepia: 70,
            brightness: 90,
            contrast: 110,
        }
    },
    { 
        name: '清新', 
        icon: <Icon.Sun size={18} />,
        filter: {
            brightness: 110,
            saturate: 120,
            contrast: 110,
            hueRotate: 10,
        }
    },
    { 
        name: '暖色', 
        icon: <Icon.Sun size={18} />,
        filter: {
            saturate: 130,
            hueRotate: 15,
            brightness: 105,
        }
    },
    { 
        name: '冷色', 
        icon: <Icon.Moon size={18} />,
        filter: {
            saturate: 110,
            hueRotate: -15,
            brightness: 100,
        }
    },
    { 
        name: '反转', 
        icon: <Icon.FlipVertical2 size={18} />,
        filter: {
            invert: 100,
            brightness: 90,
            contrast: 120,
        }
    },
    { 
        name: '柔化', 
        icon: <Icon.Circle size={18} />,
        filter: {
            blur: 1,
            brightness: 105,
            saturate: 90,
        }
    }
];

const filterControls = [
    { key: 'blur', name: '模糊', min: 0, max: 10, step: 0.1, unit: 'px', icon: <Icon.Circle size={16} /> },
    { key: 'brightness', name: '亮度', min: 0, max: 200, step: 1, unit: '%', icon: <Icon.Sun size={16} /> },
    { key: 'contrast', name: '对比度', min: 0, max: 200, step: 1, unit: '%', icon: <Icon.Moon size={16} /> },
    { key: 'grayscale', name: '灰度', min: 0, max: 100, step: 1, unit: '%', icon: <Icon.Square size={16} /> },
    { key: 'hueRotate', name: '色相', min: -180, max: 180, step: 1, unit: 'deg', icon: <Icon.Palette size={16} /> },
    { key: 'saturate', name: '饱和度', min: 0, max: 200, step: 1, unit: '%', icon: <Icon.Settings2 size={16} /> },
    { key: 'sepia', name: '复古', min: 0, max: 100, step: 1, unit: '%', icon: <Icon.RotateCcw size={16} /> },
    { key: 'invert', name: '反转', min: 0, max: 100, step: 1, unit: '%', icon: <Icon.FlipVertical2 size={16} /> },
];

const ImageFilter = observer(() => {
    const [open, setOpen] = useState(false);
    const [filterView, setFilterView] = useState('presets'); // 'presets' 或 'custom'
    
    // 当点击快捷工具中的滤镜按钮时，打开滤镜弹窗
    const handleOpenModal = () => {
        setOpen(true);
        
        // 如果之前未启用过滤镜，则打开时自动启用
        if (!stores.option.enableFilter) {
            stores.option.toggleFilter(true);
        }
    };
    
    // 应用预设滤镜
    const applyPreset = (preset) => {
        // 应用预设前先重置所有滤镜参数
        stores.option.resetFilter();
        
        // 然后应用预设的参数
        Object.keys(preset.filter).forEach(key => {
            stores.option.setFilter(key, preset.filter[key]);
        });
        
        // 确保滤镜是启用的
        stores.option.toggleFilter(true);
    };
    
    // 重置滤镜为默认值
    const resetFilters = () => {
        stores.option.resetFilter();
    };
    
    return (
        <>
            <Tooltip title="图片滤镜">
                <Button
                    type="default"
                    className="flex flex-col items-center justify-center h-auto py-2 px-3 w-[70px]"
                    onClick={handleOpenModal}
                >
                    <Icon.Settings2 size={20} useFa={true} className="mb-1 text-fuchsia-500 dark:text-fuchsia-400" />
                    <span className="text-xs">滤镜</span>
                </Button>
            </Tooltip>
            
            <Modal
                title={
                    <div className="flex items-center text-purple-600 dark:text-purple-400">
                        <Icon.Settings2 size={20} className="mr-2" />
                        <span>图片滤镜</span>
                    </div>
                }
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={700}
                bodyStyle={{ padding: "20px", maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
            >
                <Alert 
                    message="滤镜预览模式" 
                    description="所有滤镜效果会实时显示在编辑界面，预览窗口，同时支持导出和复制时自动应用您设置的所有滤镜效果。" 
                    type="info" 
                    showIcon 
                    className="mb-4"
                />
                
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                        <Switch 
                            checked={stores.option.enableFilter} 
                            onChange={(checked) => stores.option.toggleFilter(checked)}
                            className="mr-2" 
                        />
                        <span className="text-sm font-medium">
                            {stores.option.enableFilter ? '滤镜已启用' : '滤镜已禁用'}
                        </span>
                    </div>
                    <Button 
                        onClick={resetFilters}
                        icon={<Icon.RotateCcw size={16} className="mr-1" />}
                    >
                        重置滤镜
                    </Button>
                </div>
                
                <Tabs
                    activeKey={filterView}
                    onChange={setFilterView}
                    items={[
                        {
                            key: 'presets',
                            label: '滤镜预设',
                            children: (
                                <div className="grid grid-cols-4 gap-4">
                                    {filterPresets.map((preset, index) => (
                                        <div 
                                            key={index}
                                            className="flex flex-col items-center cursor-pointer p-4 border rounded-lg hover:shadow-md hover:border-purple-300 transition-all"
                                            onClick={() => applyPreset(preset)}
                                        >
                                            <div className="mb-2 text-purple-500">{preset.icon}</div>
                                            <span className="text-sm">{preset.name}</span>
                                        </div>
                                    ))}
                                </div>
                            ),
                        },
                        {
                            key: 'custom',
                            label: '自定义滤镜',
                            children: (
                                <div className="space-y-4">
                                    {filterControls.map((control) => (
                                        <div key={control.key} className="mb-2">
                                            <div className="flex items-center mb-1">
                                                <div className="text-purple-500 mr-1">{control.icon}</div>
                                                <span className="text-sm font-medium">{control.name}</span>
                                                <span className="ml-auto text-xs">
                                                    {stores.option.filter[control.key]}{control.unit}
                                                </span>
                                            </div>
                                            <Slider
                                                min={control.min}
                                                max={control.max}
                                                step={control.step}
                                                value={stores.option.filter[control.key]}
                                                onChange={(value) => stores.option.setFilter(control.key, value)}
                                                className="mb-4"
                                            />
                                        </div>
                                    ))}
                                </div>
                            ),
                        },
                    ]}
                />
                
                <div className="mt-4 border-t pt-4">
                    <div className="text-sm font-medium mb-2">预览效果</div>
                    <div 
                        className="w-full h-48 bg-cover bg-center rounded-lg shadow-inner border overflow-hidden"
                        style={{ 
                            backgroundImage: `url(${stores.editor.img?.src})`,
                            filter: stores.option.enableFilter ? stores.option.filterString : 'none'
                        }}
                    />
                </div>
            </Modal>
        </>
    );
});

export default ImageFilter; 